import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { cn } from "@/utils/cn";
import useGetProductVariants from "@babackapp/private-api-client/client/use-get-product-variants";
import { useSearchProducts } from "@babackapp/private-api-client/client/use-search-products";
import type { ProductVariantDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Button, ButtonLoading } from "@babackapp/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@babackapp/ui/components/dialog";
import { Input } from "@babackapp/ui/components/input";
import {
  AlertTriangle,
  ArrowLeft,
  Loader2,
  Search,
  ShoppingCart,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

type ExchangeVariantOverrideModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentVariantId: string;
  onConfirm: (variant: ProductVariantDto) => void | Promise<void>;
};

type SearchedProduct = {
  id: string;
  title: string;
  imageUrl?: string | null;
};

export function ExchangeVariantOverrideModal({
  open,
  onOpenChange,
  currentVariantId,
  onConfirm,
}: ExchangeVariantOverrideModalProps) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();

  const [search, setSearch] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const debouncedSetQ = useDebouncedCallback(setDebouncedQ, 250);
  const [selectedProduct, setSelectedProduct] = useState<SearchedProduct | null>(
    null,
  );
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(
    null,
  );
  const [submitting, setSubmitting] = useState(false);

  // Reset internal state every open/close cycle.
  useEffect(() => {
    if (!open) {
      setSearch("");
      setDebouncedQ("");
      setSelectedProduct(null);
      setSelectedVariantId(null);
      setSubmitting(false);
    }
  }, [open]);

  const { data: searchData, isFetching: isSearching } = useSearchProducts(
    shop.id,
    debouncedQ,
    { enabled: open && !selectedProduct && debouncedQ.trim().length > 0 },
  );

  const products = useMemo<SearchedProduct[]>(() => {
    const list = (searchData as unknown as SearchedProduct[] | undefined) ?? [];
    return list;
  }, [searchData]);

  const { data: variantsPayload, isFetching: isLoadingVariants } =
    useGetProductVariants({
      shopId: shop.id,
      productId: selectedProduct?.id ?? "",
      enabled: open && Boolean(selectedProduct?.id),
    });

  const variants = variantsPayload?.variants ?? [];
  const selectedVariant = variants.find((v) => v.id === selectedVariantId);

  async function handleConfirm() {
    if (!selectedVariant) return;
    try {
      setSubmitting(true);
      await onConfirm(selectedVariant as unknown as ProductVariantDto);
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t("returnDetail.exchangeVariantOverride.title")}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          <AlertTriangle className="size-4 shrink-0 mt-0.5 text-amber-600" />
          <span>{t("returnDetail.exchangeVariantOverride.priceWarning")}</span>
        </div>

        {!selectedProduct ? (
          <div className="flex flex-col gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  debouncedSetQ(e.target.value);
                }}
                placeholder={t(
                  "returnDetail.exchangeVariantOverride.searchPlaceholder",
                )}
                className="pl-9"
                autoFocus
              />
            </div>
            {(isSearching || debouncedQ.trim().length > 0) && (
              <div className="max-h-72 overflow-y-auto rounded-md border">
                {isSearching && (
                  <div className="flex items-center justify-center py-6 text-sm text-muted-foreground gap-2">
                    <Loader2 className="size-4 animate-spin" />
                    {t("misc.loading")}
                  </div>
                )}
                {!isSearching && products.length === 0 && (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    {t("productSelect.noResult")}
                  </div>
                )}
                {!isSearching &&
                  products.map((product) => (
                    <Button
                      key={product.id}
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        setSelectedProduct(product);
                        setSelectedVariantId(null);
                      }}
                      className="flex items-center gap-3 w-full h-auto justify-start px-3 py-2 rounded-none border-b last:border-b-0 [&_svg]:size-4"
                    >
                      {product.imageUrl ? (
                        <img
                          src={product.imageUrl}
                          alt=""
                          className="size-8 rounded object-cover shrink-0"
                        />
                      ) : (
                        <div className="size-8 rounded bg-muted flex items-center justify-center shrink-0">
                          <ShoppingCart className="text-muted-foreground" />
                        </div>
                      )}
                      <span className="text-sm truncate font-normal">
                        {product.title}
                      </span>
                    </Button>
                  ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedProduct(null);
                setSelectedVariantId(null);
              }}
              className="self-start text-muted-foreground"
            >
              <ArrowLeft />
              {t("returnDetail.exchangeVariantOverride.backToSearch")}
            </Button>

            <div className="flex items-center gap-2">
              {selectedProduct.imageUrl ? (
                <img
                  src={selectedProduct.imageUrl}
                  alt=""
                  className="size-10 rounded object-cover shrink-0"
                />
              ) : (
                <div className="size-10 rounded bg-muted flex items-center justify-center shrink-0">
                  <ShoppingCart className="size-4 text-muted-foreground" />
                </div>
              )}
              <span className="text-sm font-medium">
                {selectedProduct.title}
              </span>
            </div>

            <div className="max-h-72 overflow-y-auto rounded-md border">
              {isLoadingVariants && (
                <div className="flex items-center justify-center py-6 text-sm text-muted-foreground gap-2">
                  <Loader2 className="size-4 animate-spin" />
                  {t("misc.loading")}
                </div>
              )}
              {!isLoadingVariants && variants.length === 0 && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {t("returnDetail.exchangeVariantOverride.noVariants")}
                </div>
              )}
              {!isLoadingVariants &&
                variants.map((variant) => {
                  const isCurrent = variant.id === currentVariantId;
                  const isSelected = variant.id === selectedVariantId;
                  const outOfStock = variant.inventory_quantity <= 0;
                  return (
                    <Button
                      key={variant.id}
                      type="button"
                      variant="ghost"
                      onClick={() => setSelectedVariantId(variant.id)}
                      className={cn(
                        "flex items-center justify-between w-full h-auto px-3 py-2 rounded-none border-b last:border-b-0",
                        isSelected && "bg-accent",
                      )}
                    >
                      <span className="flex flex-col items-start">
                        <span className="text-sm font-normal">
                          {variant.title}
                          {isCurrent && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              ({t("returnDetail.exchangeVariantOverride.current")})
                            </span>
                          )}
                        </span>
                        {variant.sku && (
                          <span className="text-xs text-muted-foreground font-normal">
                            {variant.sku}
                          </span>
                        )}
                      </span>
                      <span
                        className={cn(
                          "text-xs px-2 py-0.5 rounded-full",
                          outOfStock
                            ? "bg-destructive/10 text-destructive"
                            : "bg-green-100 text-green-700",
                        )}
                      >
                        {outOfStock
                          ? t(
                              "returnDetail.exchangeVariantOverride.outOfStock",
                            )
                          : t("returnDetail.exchangeVariantOverride.inStock", {
                              count: variant.inventory_quantity,
                            })}
                      </span>
                    </Button>
                  );
                })}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            {t("misc.cancel")}
          </Button>
          <ButtonLoading
            type="button"
            onClick={handleConfirm}
            disabled={!selectedVariant}
            isLoading={submitting}
          >
            {t("returnDetail.exchangeVariantOverride.confirm")}
          </ButtonLoading>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
